import { useGet, usePost } from "@/apiHooks/HttpFunctions";
import {
  citiesApi,
  competitionsMasterDataApi,
  countriesApi,
  eventsMasterDataApi,
  termsMasterDataApi,
} from "@/apis/apis";
import AsyncDropdown from "@/components/AsyncDropdown/AsyncDropdown";
import BasicInput from "@/components/BasicInput";
import Button from "@/components/Button";
import ImageInput from "@/components/ImageInput/Index";
import PageHeaderName from "@/components/PageHeaderName/PageHeaderName";
import RenderToast from "@/components/RenderToast";
import RichTextEditor from "@/components/RichTextEditor";
import Spinner from "@/components/Spinner";
import { blogPostHistoryActionLabels } from "@/constant/Blogs/Posts/history-action";
import { LOADING_PLACEHOLDER } from "@/constant/constants";
import { HTTP_METHODS } from "@/constant/Http/methods";
import { Active, Deactivated } from "@/constant/imagePath";
import {
  DEFAULT_LANGUAGE,
  DEFAULT_LANGUAGE_LABEL,
} from "@/constant/Languages/language";
import { LIST_MODE, LIST_PER_PAGE } from "@/constant/Lists/listMode";
import {
  STATUS,
  statusBgColors,
  statusLabels,
} from "@/constant/statusConstants";
import { useLayout } from "@/context/LayoutContext";
import { useModalConfirmation } from "@/context/ModalConfirmationContext";
import { useModalInfo } from "@/context/ModalContext";
import useModalGetOptions from "@/CustomHooks/Modal/useModalGetOptions";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import useUnsavedChangesWarning from "@/hooks/useUnsavedChangesWarning";
import { cn } from "@/utils/cn";
import { formatDateTime } from "@/utils/helper";
import { Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useFormik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import RadioInput from "@/components/RadioInput/Index";
import CreatableDropdown from "@/components/Creatable/Creatable";
import { booleanOptionFormOptions } from "@/constant/GeneralOptions/boolean";
import EventSchema from "@/schema/WebDataManagement/MasterData/Events/EventSchema";
import EventInitialValues from "@/data/WebDataManagement/MasterData/Events/initial-values";

export default function EventsFormPage() {
  // Path Params
  const { id } = useParams();
  const isEdit = !!id;

  // Hooks
  const locales = useSelector(
    (state) => state?.siteConfigurationReducer?.locales
  );

  const localeCodes = useMemo(
    () => locales?.map((locale) => locale.code) ?? [],
    [locales]
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const targetLang = searchParams.get("target_lang") || DEFAULT_LANGUAGE;
  const { setIsFullPage } = useLayout();
  const { openModalConfirmation } = useModalConfirmation();
  const { openModalInfo } = useModalInfo();
  const navigate = useNavigate();
  const initCurrentCount = { char: 0, word: 0 };
  const [currentCount, setCurrentCount] = useState(initCurrentCount);
  const showApi = isEdit ? `${eventsMasterDataApi}/${id}` : null;
  const {
    data: showData,
    isFetching: showIsFetching,
    error: showError,
  } = useGet({
    apiURL: showApi,
    params: { target_lang: targetLang },
    enabled: isEdit,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
  const {
    data: showMetaData,
    isFetching: showMetaIsFetching,
    error: showMetaError,
  } = useGet({
    apiURL: `${showApi}/meta`,
    enabled: isEdit,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: EventInitialValues(
      isEdit && !showIsFetching ? showData : null
    ),
    validationSchema: EventSchema(isEdit, targetLang),
    onSubmit: (values) => handleSubmit(values),
  });

  useUnsavedChangesWarning(formik.dirty);
  const statusLabelsMap = statusLabels();
  const statusRadioOptions = [
    {
      value: STATUS.ACTIVE,
      label: statusLabelsMap[STATUS.ACTIVE],
      imageSrc: Active,
    },
    {
      value: STATUS.INACTIVE,
      label: statusLabelsMap[STATUS.INACTIVE],
      imageSrc: Deactivated,
    },
  ];

  // Update the current counts
  const updateCounts = useCallback((text) => {
    setCurrentCount({
      char: countChar(text),
      word: countWord(text),
    });
  }, []);

  // useEffects
  useEffect(() => {
    if (showError?.status === 404) {
      RenderToast({
        type: "error",
        message: "Event Profile translation not found.",
      });
    }
    if (showMetaError?.status === 404) {
      RenderToast({
        type: "error",
        message: "Failed to get all of the Event Profile translation status.",
      });
    }
  }, [showError, showMetaError]);

  useEffect(() => {
    if (targetLang) {
      const params = new URLSearchParams();
      params.set("target_lang", targetLang);
      setSearchParams(params);
    }
  }, [targetLang, setSearchParams]);

  useEffect(() => {
    setIsFullPage(false);
    return () => setIsFullPage(true);
  }, [setIsFullPage]);

  // Functions
  const triggerUnsavedChangesWarning = useUnsavedChangesWarning(formik.dirty);
  const handleLanguageTab = (event, localeCode) => {
    if (!triggerUnsavedChangesWarning()) {
      event.preventDefault();
      return;
    }

    formik.resetForm();

    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.set("target_lang", localeCode);
      return newParams;
    });
  };
  const getTextFromHtml = (html) => {
    const editor = new Editor({
      extensions: [StarterKit],
      content: html,
    });
    return editor.getText();
  };
  const handleSubmit = (values) => {
    const formData = new FormData();

    if (isEdit) formData.append("_method", "PUT");
    if (targetLang === DEFAULT_LANGUAGE) {
      formData.append(
        `tb_translations[${DEFAULT_LANGUAGE}][sub_title]`,
        values.sub_title
      );
      formData.append(
        `tb_translations[${DEFAULT_LANGUAGE}][content]`,
        values.content
      );
      formData.append(
        `tb_translations[${DEFAULT_LANGUAGE}][history_status]`,
        values.history_status
      );

      formData.append("competition_id", values.competition_id);
      formData.append("term_id", values.term_id);
      formData.append("host_country", values.host_country);
      formData.append("start_date", values.start_date);
      formData.append("end_date", values.end_date);
      formData.append("final_city", values.final_city);
      formData.append("final_stadium", values.final_stadium);
      formData.append("show_in_profile", values.show_in_profile);
      formData.append("auto_translate", values.auto_translate);

      if (values.thumbnail_url && values.thumbnail_url instanceof File)
        formData.append("thumbnail_url", values.thumbnail_url);
      if (values.banner_url && values.banner_url instanceof File)
        formData.append("banner_url", values.banner_url);

      save(formData);

      return;
    } else {
      formData.append("_method", "PATCH");
      formData.append("target_lang", targetLang);
      formData.append("sub_title", values.sub_title);
      formData.append("content", values.content);
      formData.append("history_status", values.history_status);
    }

    saveTranslation(formData);
  };

  const handleTranslate = () => {
    openModalConfirmation({
      title: "It will overwrite any existing text.",
      customMutation: {
        confirmationMessage: "Are you sure you want to translate the text?",
        apiURL: `${eventsMasterDataApi}/translate/${id}?target_lang=${targetLang}`,
        action: HTTP_METHODS.PUT,
        onSuccess: () => {
          openModalInfo("Event Profile translated successfully.");
        },
        onError: (error) => {
          RenderToast({
            type: "error",
            message:
              error.response.data.errors.error ?? "Something went wrong.",
          });
        },
      },
    });
  };
  const booleanOptionFormOptionsMap = booleanOptionFormOptions();

  const handleSuccess = (action) => (data) => {
    RenderToast({
      type: "success",
      message: `Event Profile ${action} successfully.`,
    });
    if (!isEdit) navigate("/web-data-management/master-data/events");
  };
  const countChar = (text = "") => text.replace(/[\r\n]/g, "").length;
  const countWord = (text = "") =>
    text.trim().split(/\s+/).filter(Boolean).length;

  // Init the current content counts
  useEffect(() => {
    const content = getTextFromHtml(showData?.data?.content);
    updateCounts(content);
  }, [showData?.data?.content, updateCounts]);

  // Save form
  const {
    mutate: save,
    isPending: saveIsPending,
    error: saveError,
  } = usePost({
    isFormData: true,
    apiURL: isEdit ? `${eventsMasterDataApi}/${id}` : eventsMasterDataApi,
    onSuccess: handleSuccess(isEdit ? "edited" : "added"),
  });
  const {
    mutate: saveTranslation,
    isPending: saveTranslationIsPending,
    error: saveTranslationError,
  } = usePost({
    isFormData: true,
    apiURL: `${eventsMasterDataApi}/translate-overwritten/${id}`,
    onSuccess: handleSuccess("edited"),
  });

  // Get dropdown data
  const baseQueryParams = `?mode=${LIST_MODE.SELECT}&perPage=${LIST_PER_PAGE}&filter[keyword]=`;
  const countryListApi = `${countriesApi}${baseQueryParams}`;
  const cityListApi = `${citiesApi}${baseQueryParams}`;
  const competitionListApi = `${competitionsMasterDataApi}${baseQueryParams}`;
  const termListApi = `${termsMasterDataApi}${baseQueryParams}`;

  const {
    loading: countryLoading,
    defaultOptions: countryDefaultOptions,
    debouncedFetchOptions: countryFetchOptions,
    getSelectedOption: countryGetSelectedOption,
  } = useModalGetOptions({
    modalState: {
      isOpen: targetLang === DEFAULT_LANGUAGE,
    },
    listApi: countryListApi,
    formikValue: formik.values.host_country,
    formikInitialValue: formik.initialValues.host_country,
    showData:
      isEdit && targetLang === DEFAULT_LANGUAGE && showData?.data?.host_country,
  });

  const {
    loading: competitionLoading,
    defaultOptions: competitionDefaultOptions,
    debouncedFetchOptions: competitionFetchOptions,
    getSelectedOption: competitionGetSelectedOption,
  } = useModalGetOptions({
    modalState: {
      isOpen: targetLang === DEFAULT_LANGUAGE,
    },
    listApi: competitionListApi,
    formikValue: formik.values.competition_id,
    formikInitialValue: formik.initialValues.competition_id,
    showData:
      isEdit && targetLang === DEFAULT_LANGUAGE && showData?.data?.competition,
  });

  const {
    loading: termLoading,
    defaultOptions: termDefaultOptions,
    debouncedFetchOptions: termFetchOptions,
    getSelectedOption: termGetSelectedOption,
  } = useModalGetOptions({
    modalState: {
      isOpen: targetLang === DEFAULT_LANGUAGE,
    },
    listApi: termListApi,
    formikValue: formik.values.term_id,
    formikInitialValue: formik.initialValues.term_id,
    showData: isEdit && targetLang === DEFAULT_LANGUAGE && showData?.data?.term,
  });

  const {
    loading: cityLoading,
    defaultOptions: cityDefaultOptions,
    debouncedFetchOptions: cityFetchOptions,
    getSelectedOption: cityGetSelectedOption,
  } = useModalGetOptions({
    modalState: {
      isOpen: targetLang === DEFAULT_LANGUAGE,
    },
    listApi: cityListApi,
    formikValue: formik.values.final_city,
    formikInitialValue: formik.initialValues.final_city,
    showData:
      isEdit && targetLang === DEFAULT_LANGUAGE && showData?.data?.final_city,
  });

  const statusPerLanguage = showMetaData?.data?.status_per_language;
  const defaultLanguage = showMetaData?.data?.default_language;
  const content = getTextFromHtml(defaultLanguage?.content);
  const dlContentCharCount = countChar(content);
  const dlContentWordCount = countWord(content);

  // Meta data
  const Meta = () => {
    const meta = showData?.data?.meta;
    const currentState = meta?.history;
    const lastOverwrite = meta?.last_overwrite;
    const lastTranslation = meta?.last_translation;
    const currentCharCount = currentCount?.char || 0;
    const currentWordCount = currentCount?.word || 0;

    return (
      <Row className="flex items-end border-b-2 border-gray-100 pb-2">
        <Col md={12}>
          <div className="text-jersium-black font-extralight text-sm grid grid-cols-2">
            <div>
              {/* Current state action */}
              {currentState &&
                (targetLang === DEFAULT_LANGUAGE ? (
                  <div className="text-red-700">
                    Original, used for Translation
                  </div>
                ) : (
                  <div>
                    {blogPostHistoryActionLabels[currentState.action] ?? null}{" "}
                    by {currentState.by}
                  </div>
                ))}

              {/* Last overwrite */}
              <div>
                {targetLang === DEFAULT_LANGUAGE
                  ? "Last Change: "
                  : "Last Overwrite: "}

                {lastOverwrite
                  ? `${formatDateTime(lastOverwrite.date)} by ${
                      lastOverwrite.by
                    }`
                  : "-"}
              </div>

              {/* Last translation */}
              <div>
                {targetLang !== DEFAULT_LANGUAGE &&
                  `Last Translation: ${
                    lastTranslation ? formatDateTime(lastTranslation.date) : "-"
                  }`}
              </div>
            </div>

            <div className="pl-3">
              {/* Counts */}
              <div>
                Character Count ({DEFAULT_LANGUAGE_LABEL} / Current):{" "}
                {!showMetaData || showMetaIsFetching ? "-" : dlContentCharCount}{" "}
                / {currentCharCount}
              </div>
              <div>
                Word Count ({DEFAULT_LANGUAGE_LABEL} / Current):{" "}
                {!showMetaData || showMetaIsFetching ? "-" : dlContentWordCount}{" "}
                / {currentWordCount}
              </div>

              {/* # times translated */}
              <div>
                {targetLang !== DEFAULT_LANGUAGE &&
                  `# Times Translated: ${
                    !meta || showIsFetching
                      ? "-"
                      : currentState.translated_count
                  }`}
              </div>
            </div>
          </div>
        </Col>
      </Row>
    );
  };

  console.log(targetLang, DEFAULT_LANGUAGE, formik);

  // Default language forms
  const defaultLangForms = targetLang === DEFAULT_LANGUAGE && (
    <>
      <Row>
        <Col md={6}>
          <AsyncDropdown
            label="Competition"
            placeholder="No competition group selected. Type to search..."
            labelClass="mb-0 flex items-center"
            containerClassName="!grid-cols-6 !gap-0"
            dropdownWrapperClassName="!col-span-5 !ml-0"
            defaultOptions={competitionDefaultOptions}
            loadOptions={competitionFetchOptions}
            isLoading={competitionLoading}
            value={competitionGetSelectedOption()}
            onChange={(selected) => {
              formik.setFieldValue("competition_id", selected?.value || null);
            }}
            errorText={
              (formik.touched.competition_id && formik.errors.competition_id) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.competition_id?.join(" "))
            }
          />
        </Col>

        <Col md={6}>
          <BasicInput
            formik={formik}
            fieldName={"sub_title"}
            saveError={saveError}
            label="Sub Title"
            labelClass="mb-0 flex items-center"
            inputDivClass={"grid grid-cols-6"}
            inputClass={"col-span-5"}
            errorTextClass={"block"}
          />
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <AsyncDropdown
            label="Term"
            placeholder="No term group selected. Type to search..."
            labelClass="mb-0 flex items-center"
            containerClassName="!grid-cols-6 !gap-0"
            dropdownWrapperClassName="!col-span-5 !ml-0"
            defaultOptions={termDefaultOptions}
            loadOptions={termFetchOptions}
            isLoading={termLoading}
            value={termGetSelectedOption()}
            onChange={(selected) => {
              formik.setFieldValue("term_id", selected?.value || null);
            }}
            errorText={
              (formik.touched.term_id && formik.errors.term_id) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.term_id?.join(" "))
            }
          />
        </Col>

        <Col md={6}>
          <AsyncDropdown
            required={false}
            label="Host Country"
            placeholder="No country selected. Type to search..."
            labelClass="mb-0 flex items-center"
            containerClassName="!grid-cols-6 !gap-0"
            dropdownWrapperClassName="!col-span-5 !ml-0"
            defaultOptions={countryDefaultOptions}
            loadOptions={countryFetchOptions}
            isLoading={countryLoading}
            value={countryGetSelectedOption()}
            onChange={(selected) => {
              formik.setFieldValue(
                "host_country",
                selected?.data?.name || formik.values.host_country || null
              );
            }}
            errorText={
              (formik.touched.host_country && formik.errors.host_country) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.host_country?.join(" "))
            }
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <BasicInput
            required={false}
            type="date"
            formik={formik}
            fieldName={"start_date"}
            saveError={saveError}
            label="Start Date"
            labelClass="mb-0 flex items-center"
            inputDivClass={"grid grid-cols-6"}
            inputClass={"col-span-5"}
            errorTextClass={"block"}
          />
        </Col>

        <Col md={6}>
          <CreatableDropdown
            required={false}
            isAsync
            label="Final City"
            name="city"
            className="mb-4"
            placeholder="No city selected. Type to search..."
            labelClass="mb-0 flex items-center"
            containerClassName="!grid-cols-6 !gap-0"
            dropdownWrapperClassName="!col-span-5 !ml-0"
            defaultOptions={cityDefaultOptions}
            loadOptions={cityFetchOptions}
            isLoading={cityLoading}
            value={cityGetSelectedOption()}
            onChange={(selected) => {
              formik.setFieldValue(
                "final_city",
                selected?.data?.name ||
                  selected?.value ||
                  formik.values.city ||
                  null
              );
            }}
            errorText={
              (formik.touched.final_city && formik.errors.final_city) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.final_city?.join(" "))
            }
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <BasicInput
            required={false}
            type="date"
            formik={formik}
            fieldName={"end_date"}
            saveError={saveError}
            label="End Date"
            labelClass="mb-0 flex items-center"
            inputDivClass={"grid grid-cols-6"}
            inputClass={"col-span-5"}
            errorTextClass={"block"}
          />
        </Col>

        <Col md={6}>
          <BasicInput
            required={false}
            formik={formik}
            fieldName={"final_stadium"}
            saveError={saveError}
            label="Final Stadium"
            labelClass="mb-0 flex items-center"
            inputDivClass={"grid grid-cols-6"}
            inputClass={"col-span-5"}
            errorTextClass={"block"}
          />
        </Col>
      </Row>
      <Row>
        <Col md={6} className="text-left"></Col>
        <Col md={6} className="text-left">
          <RadioInput
            options={booleanOptionFormOptionsMap}
            formik={formik}
            saveError={saveError}
            fieldName={"show_in_profile"}
            label={"Show Profile?"}
          />
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <ImageInput
            required={false}
            id="banner_url"
            formFormik={formik}
            fieldName="banner_url"
            label="Banner"
            labelClass="mb-0 flex items-center"
            error={saveError}
            existingImageUrl={isEdit && showData?.data?.banner_url}
            clickablePreview={true}
            customStyle={{ width: "100%" }}
            imagePreviewWrapper={"col-span-5"}
            className={"grid grid-cols-6"}
            inputContainerClass={"col-span-5 border-0"}
            imagePreviewContainer={"grid grid-cols-6 gap-3"}
            errorTextClass={"ml-0"}
          />
        </Col>
        <Col md={6}>
          <ImageInput
            required={false}
            id="thumbnail_url"
            formFormik={formik}
            fieldName="thumbnail_url"
            label="Thumbnail"
            labelClass="mb-0 flex items-center"
            error={saveError}
            existingImageUrl={isEdit && showData?.data?.thumbnail_url}
            clickablePreview={true}
            customStyle={{ width: "100%" }}
            imagePreviewWrapper={"col-span-5"}
            className={"grid grid-cols-6"}
            inputContainerClass={"col-span-5 border-0"}
            imagePreviewContainer={"grid grid-cols-6 gap-3"}
            errorTextClass={"ml-0"}
          />
        </Col>
      </Row>
    </>
  );

  const isPending = saveIsPending || saveTranslationIsPending;
  const error = saveError || saveTranslationError;

  // Error handlers
  useFormikErrorToast(formik);
  useHttpErrorToast(error);

  return (
    <>
      <div className="-mt-1.5">
        <PageHeaderName
          title={isEdit ? "Edit Event Profile" : "Add Event Profile"}
        />
      </div>

      <ul className="flex flex-grow text-sm font-medium text-center text-white border-b-2 border-gray-100 mt-2 mb-2.5 w-full">
        {locales.map((locale) => (
          <li key={locale.code}>
            <button
              type="button"
              aria-current="page"
              className={cn(
                "flex gap-x-2 items-center px-3 py-2 rounded-t-lg text-nowrap disabled:cursor-not-allowed",
                targetLang === locale.code
                  ? "text-jersium-black bg-gray-100"
                  : "text-gray-500 disabled:text-gray-300 enabled:hover:bg-gray-100"
              )}
              onClick={(e) => handleLanguageTab(e, locale.code)}
              disabled={!id && locale.code !== DEFAULT_LANGUAGE}
            >
              {/* Team status circle indicator */}
              {!isEdit && locale.code !== DEFAULT_LANGUAGE ? (
                <span className="inline-block w-2 h-2 rounded-full bg-gray-100" />
              ) : (
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{
                    background:
                      statusBgColors[
                        statusPerLanguage?.[locale.code] || STATUS.INACTIVE
                      ],
                  }}
                />
              )}

              {/* The locale */}
              <span>
                {locale.code.toUpperCase()}
                {targetLang === locale.code && formik.dirty ? "*" : " "}
              </span>

              {/* Loading spinner */}
              {targetLang === locale.code && showIsFetching && (
                <Spinner color="gray" />
              )}
            </button>
          </li>
        ))}
      </ul>

      {!(isEdit && showIsFetching) && (
        <form
          onSubmit={formik ? formik.handleSubmit : null}
          className="overflow-hidden flex gap-y-2 flex-col justify-center items-center w-full"
        >
          <div className="flex-1 flex flex-col gap-y-2 w-full">
            <Meta />
            <Row className="border-b-2 border-gray-100 pb-2 mb-2">
              <Col md={12}>
                <div className="mt-2 w-full flex justify-between">
                  <div className="gap-x-6 flex justify-start items-center">
                    <label className="block text-sm me-4">
                      Language Status
                    </label>
                    <div className="flex gap-x-2 items-center">
                      {statusRadioOptions.map((option) => (
                        <label key={option.value} className="flex gap-2">
                          <input
                            id={`status-${option.value}`}
                            type="radio"
                            value={option.value}
                            checked={
                              formik.values.history_status === option.value
                            }
                            onChange={() =>
                              formik.setFieldValue(
                                "history_status",
                                option.value
                              )
                            }
                          />
                          <div
                            className={cn(
                              "flex items-center gap-2 px-3 py-1 h-8 rounded-md",
                              option.value === STATUS.ACTIVE
                                ? "active-status"
                                : "deActive-status"
                            )}
                          >
                            <span className="text-sm font-extralight">
                              {option.label}
                            </span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="gap-x-6 flex justify-start">
                    <div className="flex justify-end items-center gap-2.5 h-9">
                      {targetLang !== DEFAULT_LANGUAGE ? (
                        <Button
                          variant="green-gradient"
                          label={`${
                            isPending ? LOADING_PLACEHOLDER : "Translate"
                          }`}
                          onClick={handleTranslate}
                          type="button"
                          disabled={isPending}
                          className="!w-[200px] !text-[14px]"
                        />
                      ) : (
                        <>
                          <div className="flex my-auto">
                            <input
                              id="auto_translate"
                              type="checkbox"
                              value={formik.values.auto_translate}
                              checked={formik.values.auto_translate}
                              className={`h-3 w-3 border rounded-md ${
                                formik.touched.auto_translate &&
                                formik.errors.auto_translate
                                  ? "border-red-500 bg-red-50"
                                  : "border-gray-300"
                              }`}
                              onChange={(event) =>
                                formik.setFieldValue(
                                  "auto_translate",
                                  event.target.checked
                                )
                              }
                            />
                          </div>
                          <label
                            htmlFor="auto_translate"
                            className="block text-sm"
                          >
                            Translate All
                          </label>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="gap-x-6 flex justify-start">
                    <div>
                      <Button
                        variant="outlined"
                        label="Cancel/Back"
                        onClick={() =>
                          navigate("/web-data-management/master-data/teams")
                        }
                        type="button"
                        disabled={isPending}
                        className="!w-[200px] !text-[14px]"
                      />
                    </div>
                    <div>
                      <Button
                        variant="green-gradient"
                        label={`${isPending ? LOADING_PLACEHOLDER : "Save"}`}
                        type="submit"
                        disabled={isPending}
                        className="!w-[200px] !text-[14px]"
                      />
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            {targetLang !== DEFAULT_LANGUAGE && (
              <Row>
                <Col md={6}>
                  <BasicInput
                    formik={formik}
                    fieldName={"sub_title"}
                    saveError={saveError}
                    label="Sub Title"
                    labelClass="mb-0 flex items-center"
                    inputDivClass={"grid grid-cols-6"}
                    inputClass={"col-span-5"}
                    errorTextClass={"block"}
                  />
                </Col>
              </Row>
            )}

            {targetLang === DEFAULT_LANGUAGE && defaultLangForms}
            <Row>
              <Col md={12}>
                <RichTextEditor
                  formik={formik}
                  fieldName={"content"}
                  saveError={saveError}
                  label={""}
                  enableCharCounter={false}
                  enableWordCounter={false}
                  onChange={(editor) => {
                    const editorText = editor.getText();
                    updateCounts(editorText);
                    const newContent = editor.getHTML();
                    formik.setFieldValue("content", newContent);
                  }}
                />
              </Col>
            </Row>
          </div>
        </form>
      )}
    </>
  );
}
